import { NavLink, useLocation } from "react-router-dom";
import {
    LayoutDashboard, Building, Hammer, Recycle, Factory, Scissors,
    Briefcase, Calculator, FileText, Wallet, Package, Users, ScrollText,
    Settings, ChevronRight, TrendingUp, Sparkles, BookOpen, FileSpreadsheet, Truck, ClipboardList, X, Cog,
} from "lucide-react";
import { useAuth, hasRole } from "@/lib/auth";
import TID from "@/constants/testIds";

const ROLES = {
    ALL: ["CORPORATE_ADMIN", "FINANCE_PRICING", "DEPT_HEAD", "PLANT_MANAGER", "PURCHASE_MANAGER", "DIRECTOR"],
    ADMIN: ["CORPORATE_ADMIN"],
    ADMIN_FINANCE: ["CORPORATE_ADMIN", "FINANCE_PRICING"],
    ADMIN_FINANCE_DH: ["CORPORATE_ADMIN", "FINANCE_PRICING", "DEPT_HEAD"],
    ADMIN_FINANCE_DIR: ["CORPORATE_ADMIN", "FINANCE_PRICING", "DIRECTOR"],
    ADMIN_PLANT: ["CORPORATE_ADMIN", "PLANT_MANAGER"],
    ADMIN_PURCHASE: ["CORPORATE_ADMIN", "PURCHASE_MANAGER"],
};

const NAV = [
    {
        section: "WORKSPACE",
        items: [
            { key: "dashboard", to: "/", label: "Dashboard", icon: LayoutDashboard, roles: ROLES.ALL, end: true },
        ],
    },
    {
        section: "MODULES",
        items: [
            { key: "zbc", to: "/zbc", label: "ZBC Costing", icon: Calculator, roles: ROLES.ADMIN_FINANCE },
            { key: "variance-report", to: "/zbc/variance-report", label: "Variance Report", icon: TrendingUp, roles: ROLES.ADMIN_FINANCE_DH },
            { key: "supplier-compare", to: "/zbc/supplier-compare", label: "Supplier Compare", icon: FileSpreadsheet, roles: ROLES.ADMIN_FINANCE },
            // TATA Demo View entry hidden — kept in code for future re-enable (see /app/backend/routes/... tata-demo).
            // { key: "tata-demo", to: "/zbc/demo/tata", label: "TATA Demo View", icon: Sparkles, roles: ROLES.ADMIN_FINANCE_DIR },
            { key: "rfq", to: "/rfq", label: "RFQ Pricing", icon: FileText, roles: [...ROLES.ADMIN_FINANCE_DH, "DIRECTOR"] },
            { key: "pending-approval", to: "/rfq/pending-approval", label: "Pending My Approval", icon: ChevronRight, roles: ["CORPORATE_ADMIN", "DEPT_HEAD", "DIRECTOR"] },
            { key: "settlement", to: "/settlement", label: "Settlement", icon: Wallet, roles: ROLES.ADMIN_FINANCE_DIR },
            { key: "steel-price-history", to: "/steel-price-history", label: "Steel Price History", icon: TrendingUp, roles: ROLES.ADMIN_FINANCE_DIR },
            { key: "reports", to: "/reports", label: "Reports", icon: ClipboardList, roles: [...ROLES.ADMIN_FINANCE_DH, "DIRECTOR"] },
            { key: "supplier-quotes", to: "/supplier-quotes", label: "Supplier Quotes", icon: Package, roles: ROLES.ADMIN_PURCHASE },
        ],
    },
    {
        section: "FOUNDATION SETUP",
        items: [
            { key: "plants", to: "/foundation/plants", label: "Plants", icon: Building, roles: ROLES.ADMIN },
            { key: "steel-grades", to: "/foundation/steel-grades", label: "Steel Grades & Scrap", icon: Hammer, roles: ROLES.ADMIN },
            { key: "scrap-rates", to: "/foundation/scrap-rates", label: "Scrap Rates", icon: Recycle, roles: ROLES.ADMIN },
            { key: "machines", to: "/foundation/machines", label: "Machines", icon: Factory, roles: [...ROLES.ADMIN, "PLANT_MANAGER"] },
            { key: "machine-rates", to: "/foundation/machine-rates", label: "Machine Rates", icon: ChevronRight, roles: [...ROLES.ADMIN, "PLANT_MANAGER"] },
            { key: "process-rates", to: "/foundation/process-rates", label: "Process Rates", icon: Scissors, roles: ROLES.ADMIN },
            { key: "conversion-operations", to: "/foundation/conversion-operations", label: "Conversion Operations", icon: Cog, roles: ROLES.ADMIN },
            { key: "customers", to: "/foundation/customers", label: "Customers & Norms", icon: Briefcase, roles: ROLES.ADMIN },
            { key: "suppliers", to: "/foundation/suppliers", label: "Suppliers", icon: Truck, roles: ROLES.ADMIN },
            { key: "bop-catalogue", to: "/foundation/bop-catalogue", label: "BOP Catalogue", icon: Package, roles: ROLES.ADMIN },
        ],
    },
    {
        section: "ADMINISTRATION",
        items: [
            // Users & Roles entry hidden — kept in code for future re-enable.
            // { key: "users", to: "/users", label: "Users & Roles", icon: Users, roles: ROLES.ADMIN },
            { key: "audit-log", to: "/audit-log", label: "Audit Log", icon: ScrollText, roles: [...ROLES.ADMIN, "DIRECTOR"] },
            { key: "settings", to: "/settings", label: "Settings", icon: Settings, roles: ROLES.ADMIN },
            { key: "formulas", to: "/formulas", label: "Formula Registry", icon: Calculator, roles: ROLES.ADMIN_FINANCE },
            { key: "user-manual", to: "/user-manual", label: "User Manual", icon: BookOpen, roles: ROLES.ALL },
        ],
    },
];

export default function Sidebar({ mobileOpen = false, onMobileClose = () => {} }) {
    const { user, tenant } = useAuth();
    if (!user) return null;

    return (
        <>
            {/* Desktop rail — unchanged, always in the DOM, hidden below lg */}
            <aside
                data-testid={TID.sidebar}
                className="hidden lg:flex w-60 shrink-0 flex-col border-r border-slate-200 bg-white h-screen sticky top-0"
            >
                <SidebarContent user={user} tenant={tenant} />
            </aside>

            {/* Mobile drawer — overlay + slide-in panel, only below lg */}
            {mobileOpen && (
                <div className="lg:hidden">
                    <div
                        className="fixed inset-0 z-40 bg-slate-900/40"
                        onClick={onMobileClose}
                        data-testid="sidebar-mobile-backdrop"
                    />
                    <aside
                        data-testid="sidebar-mobile"
                        className="fixed inset-y-0 left-0 z-50 w-72 max-w-[85vw] flex flex-col bg-white shadow-2xl animate-fade-up"
                    >
                        <div className="flex items-center justify-end px-3 pt-3">
                            <button
                                onClick={onMobileClose}
                                className="h-8 w-8 rounded-md hover:bg-slate-100 flex items-center justify-center"
                                data-testid="sidebar-mobile-close"
                            >
                                <X className="h-4 w-4 text-slate-500" />
                            </button>
                        </div>
                        <SidebarContent user={user} tenant={tenant} onNavigate={onMobileClose} />
                    </aside>
                </div>
            )}
        </>
    );
}

function SidebarContent({ user, tenant, onNavigate }) {
    return (
        <>
            {/* Tenant logo block */}
            <div data-testid={TID.sidebarLogo} className="h-16 flex items-center gap-3 px-5 border-b border-slate-200">
                <div className="h-9 w-9 rounded-lg bg-[hsl(var(--copper))] flex items-center justify-center text-white font-display font-semibold">
                    C
                </div>
                <div className="leading-tight">
                    <div className="font-display text-[15px] font-semibold text-slate-900">CostIQ</div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">
                        {tenant?.tenant_name?.split(" ")[0] || "ABC COMPANY"} · Cost Intel
                    </div>
                </div>
            </div>

            <nav className="flex-1 overflow-y-auto py-3">
                {NAV.map((section) => {
                    const visible = section.items.filter((it) => hasRole(user, ...it.roles));
                    if (visible.length === 0) return null;
                    return (
                        <div key={section.section} className="mb-4">
                            <div className="eyebrow px-5 pt-2 pb-1.5 text-slate-400">
                                {section.section}
                            </div>
                            {visible.map((it) => (
                                <NavItem key={it.key} item={it} onNavigate={onNavigate} />
                            ))}
                        </div>
                    );
                })}
            </nav>

            <div className="border-t border-slate-200 px-5 py-3 text-[11px] text-slate-500">
                <div className="font-mono">v1.0.0 · {new Date().getFullYear()}</div>
                <div className="text-[10px] mt-0.5">Kairoo Technologies</div>
            </div>
        </>
    );
}

function NavItem({ item, onNavigate }) {
    const Icon = item.icon;
    return (
        <NavLink
            to={item.to}
            end={item.end}
            onClick={onNavigate}
            data-testid={TID.sidebarItem(item.key)}
            className={({ isActive }) =>
                [
                    "nav-active-indicator group flex items-center gap-2.5 h-9 px-3 mx-2 rounded-md text-[13px] transition-colors",
                    isActive
                        ? "is-active bg-[hsl(var(--copper-soft))] text-slate-900 font-medium"
                        : "text-slate-700 hover:bg-slate-100",
                ].join(" ")
            }
        >
            {({ isActive }) => (
                <>
                    <Icon
                        className={`h-4 w-4 ${isActive ? "text-[hsl(var(--copper))]" : "text-slate-600 group-hover:text-slate-900"}`}
                        strokeWidth={2}
                    />
                    <span className="truncate">{item.label}</span>
                </>
            )}
        </NavLink>
    );
}
